import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useBootstrapStore } from "@/store/bootstrap.store";
import { useTranslation } from "@/hooks/useTranslation";

// ── Helpers ───────────────────────────────────

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

interface PasswordCriterion {
  labelKey: string;
  met: boolean;
}

function getPasswordCriteria(pw: string): PasswordCriterion[] {
  return [
    { labelKey: "auth.passwordStrength.atLeast8", met: pw.length >= 8 },
    { labelKey: "auth.passwordStrength.uppercase", met: /[A-Z]/.test(pw) },
    { labelKey: "auth.passwordStrength.lowercase", met: /[a-z]/.test(pw) },
    { labelKey: "auth.passwordStrength.number", met: /\d/.test(pw) },
  ];
}

// ── Step indicators ───────────────────────────

function StepIndicator({
  step,
  current,
  label,
}: {
  step: number;
  current: number;
  label: string;
}) {
  const done = step < current;
  const active = step === current;
  return (
    <div className="flex items-center gap-2">
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0"
        style={{
          background: done
            ? "var(--success)"
            : active
              ? "var(--accent)"
              : "var(--bg-hover)",
          color: done || active ? "#fff" : "var(--text-muted)",
          border: active ? "2px solid var(--accent)" : "none",
        }}
      >
        {done ? "✓" : step}
      </div>
      <span
        className="text-sm"
        style={{
          color: active
            ? "var(--text-primary)"
            : done
              ? "var(--success)"
              : "var(--text-muted)",
          fontWeight: active ? 600 : 400,
        }}
      >
        {label}
      </span>
    </div>
  );
}

// ── Step 1: Deployment Mode ───────────────────

function Step1DeploymentMode({
  value,
  onChange,
}: {
  value: "SINGLE_ORG" | "MULTI_ORG" | null;
  onChange: (v: "SINGLE_ORG" | "MULTI_ORG") => void;
}) {
  const { t } = useTranslation();
  const options: { id: "SINGLE_ORG" | "MULTI_ORG"; title: string; description: string }[] = [
    {
      id: "SINGLE_ORG",
      title: t("setup.singleOrg"),
      description: t("setup.singleOrgDesc"),
    },
    {
      id: "MULTI_ORG",
      title: t("setup.multiOrg"),
      description: t("setup.multiOrgDesc"),
    },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h2
          className="text-lg font-semibold mb-1"
          style={{ color: "var(--text-primary)" }}
        >
          {t("setup.deploymentMode")}
        </h2>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          {t("setup.deploymentModeDesc")}
        </p>
      </div>
      <div className="grid grid-cols-1 gap-3">
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={cn(
              "w-full text-left p-4 rounded-lg transition-all border-2"
            )}
            style={{
              background:
                value === opt.id ? "var(--accent-bg)" : "var(--bg-input)",
              borderColor:
                value === opt.id ? "var(--accent)" : "var(--border-default)",
              color: "var(--text-primary)",
            }}
          >
            <div className="font-medium text-sm">{opt.title}</div>
            <div
              className="text-xs mt-1"
              style={{ color: "var(--text-secondary)" }}
            >
              {opt.description}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Step 2: Organization ──────────────────────

function Step2Organization({
  orgName,
  orgSlug,
  onChangeName,
  onChangeSlug,
}: {
  orgName: string;
  orgSlug: string;
  onChangeName: (v: string) => void;
  onChangeSlug: (v: string) => void;
}) {
  const { t } = useTranslation();
  const handleNameChange = (v: string) => {
    onChangeName(v);
    onChangeSlug(slugify(v));
  };

  return (
    <div className="space-y-4">
      <div>
        <h2
          className="text-lg font-semibold mb-1"
          style={{ color: "var(--text-primary)" }}
        >
          {t("setup.orgDetails")}
        </h2>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          {t("setup.orgDetailsDesc")}
        </p>
      </div>
      <div>
        <label
          className="block text-sm font-medium mb-1"
          style={{ color: "var(--text-secondary)" }}
        >
          {t("setup.orgName")}
        </label>
        <input
          type="text"
          value={orgName}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder={t("setup.orgNamePlaceholder")}
          required
          className="w-full px-3 py-2 rounded-md text-sm outline-none focus:ring-2"
          style={{
            background: "var(--bg-input)",
            color: "var(--text-primary)",
            border: "1px solid var(--border-default)",
          }}
        />
      </div>
      <div>
        <label
          className="block text-sm font-medium mb-1"
          style={{ color: "var(--text-secondary)" }}
        >
          {t("setup.slug")}
        </label>
        <input
          type="text"
          value={orgSlug}
          onChange={(e) => onChangeSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
          placeholder={t("setup.slugPlaceholder")}
          required
          className="w-full px-3 py-2 rounded-md text-sm font-mono outline-none focus:ring-2"
          style={{
            background: "var(--bg-input)",
            color: "var(--text-primary)",
            border: "1px solid var(--border-default)",
          }}
        />
        <p
          className="text-xs mt-1"
          style={{ color: "var(--text-muted)" }}
        >
          {t("setup.slugHint")}
        </p>
      </div>
    </div>
  );
}

// ── Step 3: Admin User ────────────────────────

function Step3AdminUser({
  adminName,
  adminEmail,
  adminPassword,
  onChangeName,
  onChangeEmail,
  onChangePassword,
}: {
  adminName: string;
  adminEmail: string;
  adminPassword: string;
  onChangeName: (v: string) => void;
  onChangeEmail: (v: string) => void;
  onChangePassword: (v: string) => void;
}) {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const criteria = getPasswordCriteria(adminPassword);

  return (
    <div className="space-y-4">
      <div>
        <h2
          className="text-lg font-semibold mb-1"
          style={{ color: "var(--text-primary)" }}
        >
          {t("setup.adminAccount")}
        </h2>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          {t("setup.adminAccountDesc")}
        </p>
      </div>
      <div>
        <label
          className="block text-sm font-medium mb-1"
          style={{ color: "var(--text-secondary)" }}
        >
          {t("settings.fullName")}
        </label>
        <input
          type="text"
          value={adminName}
          onChange={(e) => onChangeName(e.target.value)}
          placeholder={t("users.namePlaceholder")}
          required
          className="w-full px-3 py-2 rounded-md text-sm outline-none focus:ring-2"
          style={{
            background: "var(--bg-input)",
            color: "var(--text-primary)",
            border: "1px solid var(--border-default)",
          }}
        />
      </div>
      <div>
        <label
          className="block text-sm font-medium mb-1"
          style={{ color: "var(--text-secondary)" }}
        >
          {t("auth.email")}
        </label>
        <input
          type="email"
          value={adminEmail}
          onChange={(e) => onChangeEmail(e.target.value)}
          placeholder={t("setup.adminEmailPlaceholder")}
          required
          className="w-full px-3 py-2 rounded-md text-sm outline-none focus:ring-2"
          style={{
            background: "var(--bg-input)",
            color: "var(--text-primary)",
            border: "1px solid var(--border-default)",
          }}
        />
      </div>
      <div>
        <label
          className="block text-sm font-medium mb-1"
          style={{ color: "var(--text-secondary)" }}
        >
          {t("auth.password")}
        </label>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            value={adminPassword}
            onChange={(e) => onChangePassword(e.target.value)}
            placeholder={t("setup.passwordPlaceholder")}
            required
            className="w-full px-3 py-2 rounded-md text-sm outline-none focus:ring-2"
            style={{
              background: "var(--bg-input)",
              color: "var(--text-primary)",
              border: "1px solid var(--border-default)",
            }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs"
            style={{ color: "var(--text-muted)" }}
          >
            {showPassword ? t("auth.hide") : t("auth.show")}
          </button>
        </div>
        {adminPassword && (
          <ul className="mt-2 flex flex-col gap-0.5">
            {criteria.map((c, i) => (
              <li
                key={i}
                className="flex items-center gap-1.5 text-xs"
                style={{ color: c.met ? "var(--success)" : "var(--text-muted)" }}
              >
                <span>{c.met ? "✓" : "○"}</span>
                {t(c.labelKey)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── Step 4: Summary ───────────────────────────

function Step4Summary({
  deploymentMode,
  orgName,
  orgSlug,
  adminName,
  adminEmail,
}: {
  deploymentMode: "SINGLE_ORG" | "MULTI_ORG" | null;
  orgName: string;
  orgSlug: string;
  adminName: string;
  adminEmail: string;
}) {
  const { t } = useTranslation();
  const rows = [
    { label: t("setup.deploymentMode"), value: deploymentMode === "SINGLE_ORG" ? t("setup.singleOrg") : t("setup.multiOrg") },
    { label: t("setup.orgName"), value: orgName },
    { label: t("setup.orgSlug"), value: orgSlug },
    { label: t("setup.adminName"), value: adminName },
    { label: t("setup.adminEmail"), value: adminEmail },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h2
          className="text-lg font-semibold mb-1"
          style={{ color: "var(--text-primary)" }}
        >
          {t("setup.reviewSetup")}
        </h2>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          {t("setup.reviewSetupDesc")}
        </p>
      </div>
      <div
        className="rounded-lg overflow-hidden"
        style={{ border: "1px solid var(--border-default)" }}
      >
        {rows.map((row, i) => (
          <div
            key={row.label}
            className="flex items-center px-4 py-3"
            style={{
              borderBottom:
                i < rows.length - 1 ? "1px solid var(--border-default)" : undefined,
              background:
                i % 2 === 0 ? "var(--bg-input)" : "var(--bg-card)",
            }}
          >
            <span
              className="text-sm w-44 flex-shrink-0"
              style={{ color: "var(--text-muted)" }}
            >
              {row.label}
            </span>
            <span
              className="text-sm font-medium"
              style={{ color: "var(--text-primary)" }}
            >
              {row.value}
            </span>
          </div>
        ))}
      </div>
      <p className="text-xs" style={{ color: "var(--text-muted)" }}>
        {t("setup.completeHint", { action: t("setup.completeSetup") })}
      </p>
    </div>
  );
}

// ── SetupWizard ───────────────────────────────

export function SetupWizard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { bootstrapped, isChecking, checkBootstrap } = useBootstrapStore();

  const [step, setStep] = useState(1);
  const [deploymentMode, setDeploymentMode] = useState<
    "SINGLE_ORG" | "MULTI_ORG" | null
  >(null);
  const [orgName, setOrgName] = useState("");
  const [orgSlug, setOrgSlug] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Check bootstrap status on mount — if already done, redirect to login
  useEffect(() => {
    checkBootstrap();
  }, []); // checkBootstrap is a stable Zustand action

  useEffect(() => {
    if (bootstrapped === true) {
      navigate("/login", { replace: true });
    }
  }, [bootstrapped, navigate]);

  if (isChecking || bootstrapped === null) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "var(--bg-page)",
          color: "var(--text-muted)",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "14px",
        }}
      >
        {t("setup.checkingStatus")}
      </div>
    );
  }

  // ── Step validation ───────────────────────────

  const canProceedStep1 = deploymentMode !== null;
  const canProceedStep2 =
    orgName.trim().length > 0 && /^[a-z0-9-]+$/.test(orgSlug) && orgSlug.length > 0;
  const adminCriteria = getPasswordCriteria(adminPassword);
  const passwordValid = adminCriteria.filter((c) => c.met).length >= 4;
  const canProceedStep3 =
    adminName.trim().length > 0 &&
    adminEmail.includes("@") &&
    passwordValid;

  const canProceedMap: Record<number, boolean> = {
    1: canProceedStep1,
    2: canProceedStep2,
    3: canProceedStep3,
    4: true,
  };

  // ── Submit ────────────────────────────────────

  const handleComplete = async () => {
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch("/api/v1/system/bootstrap", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          deploymentMode,
          organization: { name: orgName.trim(), slug: orgSlug },
          adminUser: {
            name: adminName.trim(),
            email: adminEmail.trim(),
            password: adminPassword,
          },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          (data as { error?: { message?: string } })?.error?.message ??
            t("setup.bootstrapFailed")
        );
      }
      // Mark the store as bootstrapped synchronously so BootstrapGuard
      // does not redirect back to /setup when we navigate away.
      useBootstrapStore.setState({ bootstrapped: true, hasChecked: true });
      navigate("/login", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("setup.bootstrapFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const STEPS = [
    { label: t("setup.stepDeployment") },
    { label: t("setup.stepOrganization") },
    { label: t("setup.stepAdmin") },
    { label: t("setup.stepReview") },
  ];

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{
        background:
          "radial-gradient(ellipse at center, var(--bg-card) 0%, var(--bg-page) 70%)",
      }}
    >
      <div
        className="w-full max-w-lg p-8 rounded-lg"
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-default)",
        }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <h1
            className="font-mono text-2xl font-medium tracking-[3px]"
            style={{ color: "var(--text-primary)" }}
          >
            KIBORG
          </h1>
          <p className="mt-2 text-sm" style={{ color: "var(--text-muted)" }}>
            {t("setup.initialSetup")}
          </p>
        </div>

        {/* Step indicators */}
        <div className="flex flex-col gap-2 mb-8">
          {STEPS.map((s, i) => (
            <StepIndicator
              key={s.label}
              step={i + 1}
              current={step}
              label={s.label}
            />
          ))}
        </div>

        {/* Step content */}
        {step === 1 && (
          <Step1DeploymentMode
            value={deploymentMode}
            onChange={setDeploymentMode}
          />
        )}
        {step === 2 && (
          <Step2Organization
            orgName={orgName}
            orgSlug={orgSlug}
            onChangeName={setOrgName}
            onChangeSlug={setOrgSlug}
          />
        )}
        {step === 3 && (
          <Step3AdminUser
            adminName={adminName}
            adminEmail={adminEmail}
            adminPassword={adminPassword}
            onChangeName={setAdminName}
            onChangeEmail={setAdminEmail}
            onChangePassword={setAdminPassword}
          />
        )}
        {step === 4 && (
          <Step4Summary
            deploymentMode={deploymentMode}
            orgName={orgName}
            orgSlug={orgSlug}
            adminName={adminName}
            adminEmail={adminEmail}
          />
        )}

        {/* Error */}
        {error && (
          <div
            className="mt-4 p-3 rounded text-sm"
            style={{
              background: "var(--danger-bg)",
              color: "var(--danger)",
              border: "1px solid var(--danger)",
            }}
          >
            {error}
          </div>
        )}

        {/* Navigation */}
        <div className="mt-6 flex gap-3">
          {step > 1 && (
            <button
              type="button"
              onClick={() => setStep((s) => s - 1)}
              disabled={submitting}
              className="flex-1 py-2 rounded-md text-sm font-medium transition-colors"
              style={{
                background: "var(--bg-input)",
                color: "var(--text-secondary)",
                border: "1px solid var(--border-default)",
              }}
            >
              {t("common.back")}
            </button>
          )}
          {step < 4 ? (
            <button
              type="button"
              onClick={() => setStep((s) => s + 1)}
              disabled={!canProceedMap[step]}
              className="flex-1 py-2 rounded-md text-sm font-medium transition-colors"
              style={{
                background: "var(--accent)",
                color: "#fff",
                opacity: canProceedMap[step] ? 1 : 0.5,
                cursor: canProceedMap[step] ? "pointer" : "not-allowed",
              }}
            >
              {t("common.next")}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleComplete}
              disabled={submitting}
              className="flex-1 py-2 rounded-md text-sm font-medium transition-colors"
              style={{
                background: "var(--accent)",
                color: "#fff",
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? t("setup.settingUp") : t("setup.completeSetup")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
